import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { client } from '../api';

interface Provider {
  provider_id: string;
  npi: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface ProvidersResponse {
  providers: Provider[];
}

interface AddProvider {
  npi: string;
  first_name?: string;
  last_name?: string;
}

interface AddProvidersRequest {
  providers: AddProvider[];
}

interface UpdateProviderRequest {
  first_name?: string;
  last_name?: string;
}

/**
 * Hook to fetch providers
 */
export const useProviders = () => {
  return useQuery<ProvidersResponse>({
    queryKey: ['providers'],
    queryFn: async () => {
      const response = await client.get<ProvidersResponse>('/settings/providers');
      return response.data;
    },
  });
};

interface UseAddProvidersConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to add new providers via POST request
 */
export const useAddProviders = (config: UseAddProvidersConfig = {}) => {
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  return useMutation<void, Error, AddProvidersRequest>({
    mutationFn: async ({ providers }) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking provider add, no real request sent.');
        return;
      }
      await client.post('/settings/providers', {
        providers: providers.map((provider) => ({
          npi: provider.npi,
          ...(provider.first_name !== undefined && { first_name: provider.first_name }),
          ...(provider.last_name !== undefined && { last_name: provider.last_name }),
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

interface UseUpdateProviderConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update an existing provider's name via PATCH request
 */
export const useUpdateProvider = (providerId: string, config: UseUpdateProviderConfig = {}) => {
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  return useMutation<void, Error, UpdateProviderRequest>({
    mutationFn: async (data) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking provider update, no real request sent.');
        return;
      }
      await client.patch(`/settings/providers/${providerId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

interface RemoveProviderRequest {
  churn_reason: string;
  specify_other_churned_reason?: string;
}

interface UseRemoveProviderConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to remove a provider via DELETE request
 */
export const useRemoveProvider = (providerId: string, config: UseRemoveProviderConfig = {}) => {
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  return useMutation<void, Error, RemoveProviderRequest>({
    mutationFn: async (data) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking provider removal, no real request sent.');
        return;
      }
      await client.delete(`/settings/providers/${providerId}`, { data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};
